import React, { useEffect, useState } from "react";
import Accordion from "../ui/Accordion/Accordion";
import Option from "../ui/Option/Option";
import BottomSheetContainer from "../ui/BottomSheet/BottomSheetContainer";
import SubOption from "../ui/SubOption/SubOption";
import DocumentsRequired from "../DocumentsRequired/DocumentsRequired";
import sendEddData from "../../handlers/sendEddData";
import Loader from "../ui/Loader/Loader";

interface Props {
  section: any;
  openAccordionId: number;
  id: number;
  setOpenAccordionId: React.Dispatch<React.SetStateAction<number>>;
  enabledSectionsCount: number;
  setEnabledSectionsCount: React.Dispatch<React.SetStateAction<number>>;
  refreshEddFormData: () => void;
  setEnableSubmitButton: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSendingEddData: React.Dispatch<React.SetStateAction<boolean>>;
  isSendingEddData: boolean;
  uploadedDocuments: any[];
  setUploadedDocuments: React.Dispatch<React.SetStateAction<any[]>>;
}

const FormSection: React.FC<Props> = ({
  section,
  openAccordionId,
  id,
  setOpenAccordionId,
  enabledSectionsCount,
  setEnabledSectionsCount,
  refreshEddFormData,
  setEnableSubmitButton,
  setIsSendingEddData,
  isSendingEddData,
  uploadedDocuments,
  setUploadedDocuments
}) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [isEdited, setIsEdited] = useState<boolean>(false);
  const [othersValue, setOthersValue] = useState<string>("");
  const [subOptionOthersValue, setSubOptionOthersValue] = useState<string>("");
  const [selectedSubOptions, setSelectedSubOptions] = useState<string[]>([]);
  const [areCompulsoryDocumentsUploaded, setAreCompulsoryDocumentsUploaded] =
    useState<boolean>(false);
  const [isSubOptionNotSelected, setIsSubOptionNotSelected] =
    useState<boolean>(false);

  const {
    name,
    question,
    status,
    options,
    question_type: questionType,
    optional,
    tag,
    documents
  } = section ?? {};

  const answer = selectedOptions.length
    ? questionType === "single-option"
      ? selectedOptions?.[0] === "Others"
        ? [`${selectedOptions?.[0]}:${othersValue}`]
        : selectedSubOptions.length
          ? [
              `${selectedOptions?.[0]},${selectedSubOptions[0]}${
                selectedSubOptions[0] === "Others"
                  ? `:${subOptionOthersValue}`
                  : ""
              }`
            ]
          : [selectedOptions?.[0]]
      : questionType === "multiple-option"
        ? selectedOptions?.map(option =>
            option === "Others" ? `${option}:${othersValue}` : option
          )
        : []
    : [];

  useEffect(() => {
    if (options?.length) {
      options?.forEach(
        ({
          value,
          is_selected: isSelected,
          others_answer: othersAnswer
        }: {
          value: string;
          is_selected: boolean;
          others_answer: string;
        }) => {
          if (isSelected) {
            setSelectedOptions(prev => [...(prev ?? []), value]);
            if (value === "Others") {
              setOthersValue(othersAnswer);
            }
          }
        }
      );
    }
  }, [JSON.stringify(options)]);

  useEffect(() => {
    (async () => {
      if (
        isEdited &&
        tag !== "DECLARATION" &&
        tag !== "DOCUMENT" &&
        !isSubOptionNotSelected
      ) {
        setIsSendingEddData(true);
        const sendEddResponse = await sendEddData({
          section: tag,
          question: tag,
          answer
        });
        setIsSendingEddData(false);
        if (sendEddResponse?.success) {
          refreshEddFormData();
          setUploadedDocuments([]);
        }
      }
    })();
    setIsEdited(false);
  }, [openAccordionId]);

  useEffect(() => {
    if (
      (answer?.length ||
        (questionType === "document" && areCompulsoryDocumentsUploaded)) &&
      (selectedOptions?.includes("Others") ? othersValue : true) &&
      (selectedSubOptions?.[0] === "Others" ? subOptionOthersValue : true) &&
      !isSubOptionNotSelected
    ) {
      setEnabledSectionsCount(Math.max(id + 2, enabledSectionsCount));
    } else {
      setEnabledSectionsCount(id + 1);
      setEnableSubmitButton(false);
    }
  }, [
    JSON.stringify(answer),
    id,
    areCompulsoryDocumentsUploaded,
    isSubOptionNotSelected
  ]);

  useEffect(() => {
    if (selectedOptions?.[0] === "DECLARATION") {
      setEnableSubmitButton(true);
    } else {
      setEnableSubmitButton(false);
    }
  }, [JSON.stringify(selectedOptions)]);

  return (
    <div className="mt-5">
      <Accordion
        title={name}
        isDisabled={id > enabledSectionsCount - 1}
        openAccordionId={openAccordionId}
        id={id}
        setOpenAccordionId={setOpenAccordionId}
        isCompleted={
          status === "COMPLETED" ||
          selectedOptions?.[0] === "DECLARATION" ||
          (areCompulsoryDocumentsUploaded && tag === "DOCUMENT")
        }
      >
        {isSendingEddData ? (
          <div className="bg-white py-1">
            <Loader color="purple" />
          </div>
        ) : questionType === "document" ? (
          <DocumentsRequired
            documents={documents}
            setAreCompulsoryDocumentsUploaded={
              setAreCompulsoryDocumentsUploaded
            }
            setUploadedDocuments={setUploadedDocuments}
            setIsEdited={setIsEdited}
            uploadedDocuments={uploadedDocuments}
          />
        ) : (
          <div className="bg-white rounded-b-lg shadow p-4 mb-6">
            <form>
              <p className="mb-[10px]">
                <span className="text-14-sb text-grey-dark">{question}</span>
                {!optional && <span className="text-status-negative">*</span>}
              </p>
              {options?.map(
                ({
                  value,
                  sub_options: subOptions
                }: {
                  value: string;
                  sub_options: [];
                }) => (
                  <Option
                    questionType={questionType}
                    value={value}
                    setSelectedOptions={setSelectedOptions}
                    selectedOptions={selectedOptions}
                    subOptions={subOptions}
                    setIsEdited={setIsEdited}
                    othersValue={othersValue}
                    setOthersValue={setOthersValue}
                    subOptionOthersValue={subOptionOthersValue}
                    setSubOptionOthersValue={setSubOptionOthersValue}
                    selectedSubOptions={selectedSubOptions}
                    setSelectedSubOptions={setSelectedSubOptions}
                    tag={tag}
                    setEnabledSectionsCount={setEnabledSectionsCount}
                    accordionId={id}
                    setIsSubOptionNotSelected={setIsSubOptionNotSelected}
                  />
                )
              )}
            </form>
          </div>
        )}
      </Accordion>
    </div>
  );
};

export default FormSection;
